import { useQuery } from '@tanstack/react-query';

import { Card } from '@/components/ui';
import { cn } from '@/lib/cn';
import { costCenterApi, type MoverItem } from '@/lib/api/costCenter';

interface CostAnomalyCardProps {
  excludeTeam?: boolean;
}

function ratioClass(ratio: number | null): string {
  if (ratio === null) return 'text-muted-foreground';
  if (ratio >= 2) return 'text-destructive';
  if (ratio >= 1.5) return 'text-warning';
  return 'text-success';
}

function ratioText(ratio: number | null): string {
  return ratio === null ? '—' : `${ratio.toFixed(1)}x`;
}

function formatCny(value: number): string {
  return `¥${value.toFixed(2)}`;
}

function statusText(ratio: number | null): string {
  if (ratio === null) return '基线不足';
  if (ratio >= 2) return '成本显著异常';
  if (ratio >= 1.5) return '成本偏高';
  return '成本平稳';
}

export function CostAnomalyCard({ excludeTeam = true }: CostAnomalyCardProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['cost-anomalies', excludeTeam],
    queryFn: () => costCenterApi.getAnomalies(excludeTeam),
  });

  if (isLoading) {
    return (
      <Card>
        <p className="text-sm text-muted-foreground">加载中...</p>
      </Card>
    );
  }

  if (isError || !data) {
    return (
      <Card>
        <p className="text-sm text-muted-foreground">成本异常检测暂不可用</p>
      </Card>
    );
  }

  const baselineInsufficient = data.baseline_daily_avg_cny === 0 || data.ratio === null;

  return (
    <Card className="p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="text-base font-semibold text-foreground">成本异常检测</h3>
          <p className="mt-1 text-xs text-muted-foreground">今日(UTC) vs 前 7 日均 · 不随时间筛选</p>
        </div>
        {!baselineInsufficient && (
          <span className={cn('text-sm font-semibold', ratioClass(data.ratio))}>{statusText(data.ratio)}</span>
        )}
      </div>

      {baselineInsufficient && <p className="mt-3 text-sm text-muted-foreground">基线不足，暂无对比</p>}

      <div className="mt-5 grid gap-3 sm:grid-cols-3">
        <SummaryMetric label="今日总成本 CNY" value={formatCny(data.today_cny)} />
        <SummaryMetric label="7日均 CNY" value={formatCny(data.baseline_daily_avg_cny)} />
        <SummaryMetric label="倍率" value={ratioText(data.ratio)} valueClassName={ratioClass(data.ratio)} />
      </div>

      <div className="mt-5 grid gap-5 xl:grid-cols-2">
        <MoversTable title="用户 Top Movers" movers={data.top_users} />
        <MoversTable title="模型 Top Movers" movers={data.top_models} />
      </div>
    </Card>
  );
}

function SummaryMetric({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: string;
  valueClassName?: string;
}) {
  return (
    <div className="rounded-lg border border-border bg-surface-2 px-4 py-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={cn('mt-1 font-mono text-lg font-semibold text-foreground', valueClassName)}>{value}</div>
    </div>
  );
}

function MoversTable({ title, movers }: { title: string; movers: MoverItem[] }) {
  return (
    <section>
      <h4 className="mb-2 text-sm font-semibold text-foreground">{title}</h4>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[32rem] text-sm">
          <thead className="bg-surface-2 text-xs text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left font-medium">id</th>
              <th className="px-3 py-2 text-right font-medium">今日(CNY)</th>
              <th className="px-3 py-2 text-right font-medium">7日均(CNY)</th>
              <th className="px-3 py-2 text-right font-medium">倍率</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {movers.length === 0 ? (
              <tr>
                <td className="px-3 py-4 text-center text-xs text-muted-foreground" colSpan={4}>
                  暂无今日成本数据
                </td>
              </tr>
            ) : (
              movers.map((mover) => (
                <tr key={mover.id}>
                  <td className="max-w-48 truncate px-3 py-2 font-mono text-xs text-foreground" title={mover.id}>
                    {mover.id}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs text-foreground">
                    {mover.today_cny.toFixed(2)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs text-foreground">
                    {mover.baseline_daily_avg_cny.toFixed(2)}
                  </td>
                  <td className={cn('px-3 py-2 text-right font-mono text-xs font-semibold', ratioClass(mover.ratio))}>
                    {ratioText(mover.ratio)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
